/**
 * The workflow-run `ask_id` prefixes, declared once for every runtime.
 *
 * A workflow run writes into an assistant thread under a small set of
 * idempotency keys. Nest mints them, the thread page reads them back to place
 * study pins, step notes, gate decisions and completion announcements. Before
 * this module the eight prefixes were hand-mirrored in the frontend (one of
 * them twice), so a rename on either side silently stopped matching the rows
 * the other side had already written.
 *
 * Shape of an id: `<prefix><runToken>` plus, for the per-step lines,
 * `:<stepIndex>` and an optional `:<attempt>` suffix. Every id starts with
 * `workflow-run-`, which is what the SQL `workflow-run-%` history exclusion
 * and `isWorkflowRunAskId` match on.
 *
 * Builders and predicates stay in their owning runtime: this module is the
 * literal contract, nothing else.
 */

/** Every workflow-run ask id starts with this. */
export const WORKFLOW_RUN_ASK_ID_NAMESPACE = "workflow-run-";

/** `workflow-run-intro:<runToken>` — the intro line of a run-minted thread. */
export const WORKFLOW_RUN_INTRO_ASK_ID_PREFIX = "workflow-run-intro:";

/** `workflow-run-ask:<runToken>` — the user sentence that started the run. */
export const WORKFLOW_RUN_ASK_TURN_ASK_ID_PREFIX = "workflow-run-ask:";

/** `workflow-run-step:<runToken>:<stepIndex>[:<attempt>]` — settled-step line. */
export const WORKFLOW_RUN_STEP_ASK_ID_PREFIX = "workflow-run-step:";

/** `workflow-run-gate:<runToken>:<stepIndex>` — gate decision line. */
export const WORKFLOW_RUN_GATE_ASK_ID_PREFIX = "workflow-run-gate:";

/** `workflow-run-failed:<runToken>:<stepIndex>:<attempt>` — step failure line. */
export const WORKFLOW_RUN_FAILED_ASK_ID_PREFIX = "workflow-run-failed:";

/** `workflow-run-cancelled:<runToken>` — run cancelled line. */
export const WORKFLOW_RUN_CANCELLED_ASK_ID_PREFIX = "workflow-run-cancelled:";

/** `workflow-run-complete:<runToken>` — run completion announcement. */
export const WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX = "workflow-run-complete:";

/**
 * Study announcements use their own prefixes so a failure never occupies the
 * ready key. They are not part of `workflow-run-` and are not listed in
 * `WORKFLOW_RUN_ASK_ID_PREFIXES`.
 *
 * `study-complete:<experimentToken>` — report ready.
 * `study-failed:<experimentToken>` — run failed, or the report failed.
 */
export const STUDY_COMPLETE_ASK_ID_PREFIX = "study-complete:";
export const STUDY_FAILED_ASK_ID_PREFIX = "study-failed:";

/** `workflow-run-revision:<runToken>:<stepIndex>:<attempt>` — revision turn. */
export const WORKFLOW_RUN_REVISION_ASK_ID_PREFIX = "workflow-run-revision:";

/**
 * Every prefix, in the order the lines can appear in a thread. Used by the
 * outcome-line predicate and by the parity test that keeps both runtimes
 * reading this module instead of their own copies.
 */
export const WORKFLOW_RUN_ASK_ID_PREFIXES: ReadonlyArray<string> = [
  WORKFLOW_RUN_INTRO_ASK_ID_PREFIX,
  WORKFLOW_RUN_ASK_TURN_ASK_ID_PREFIX,
  WORKFLOW_RUN_STEP_ASK_ID_PREFIX,
  WORKFLOW_RUN_GATE_ASK_ID_PREFIX,
  WORKFLOW_RUN_FAILED_ASK_ID_PREFIX,
  WORKFLOW_RUN_CANCELLED_ASK_ID_PREFIX,
  WORKFLOW_RUN_COMPLETION_ASK_ID_PREFIX,
  WORKFLOW_RUN_REVISION_ASK_ID_PREFIX,
];

/**
 * True for any persisted workflow-run line, whatever its lane. Both runtimes
 * read this rather than re-testing `/^workflow-run-/` themselves.
 */
export function isWorkflowRunAskId(
  askId: string | null | undefined,
): boolean {
  return Boolean(askId && askId.startsWith(WORKFLOW_RUN_ASK_ID_NAMESPACE));
}
